import { useCallback, useMemo } from 'react';

import { useIsImpersonatedUser } from '$integrations/clerk/useIsImpersonatedUser';

import { _eventTracker, logger } from '../../../bootstrap.client';
import { type IdentifyUserTraits, type TrackEventDictionary, type TrackEventName } from './__dictionary__';

/**
 * The primary hook application consumers should use to track events. It's a thin wrapper around our
 * Platform Event Tracking Service and, where necessary, enhances methods with application-specific data
 *
 * For method documentation:
 * @see @littleotter/zz-platform/src/svc/event-tracking/PlatformEventTrackingService.ts
 *
 * TODO: The current implementation of the useEventTracking hook is literally duplicated across applications. This is very yucky! But we are doing it in order to move quickly at present. There will be a follow-up PR to unify this functionality at a platform level, but it will require some more careful thought and planning to account for flags and types differing between apps. For the time being, we're rolling with the duplication.
 */
export const useEventTracking = () => {
  const isImpersonatedUser = useIsImpersonatedUser();
  const VIA = 'legacy-app';

  /**
   * Enhance each track call with application-specific data
   */
  const enhancedTrack = useCallback(
    <T extends TrackEventName>(name: T, properties?: TrackEventDictionary[T], callback?: () => void) => {
      if (isImpersonatedUser) {
        logger.debug('[useEventTracking.enhancedTrack] skipping track because current user is impersonated');
        return;
      }
      _eventTracker.track(name, { ...properties, via: VIA }, callback);
    },
    [isImpersonatedUser]
  );

  /**
   * Enhance each viewedPage call with application-specific data (e.g. feature flags).
   */
  const enhancedViewedPage: typeof _eventTracker.viewedPage = useCallback(
    (properties, callback) => {
      if (isImpersonatedUser) {
        logger.debug('[useEventTracking.enhancedViewedPage] skipping viewedPage because current user is impersonated');
        return;
      }

      _eventTracker.viewedPage({ ...properties, via: VIA }, callback);
    },
    [isImpersonatedUser]
  );

  /**
   * Enhance each identify call with proper typings for user traits
   */
  const enhancedIdentify: typeof _eventTracker.identify<IdentifyUserTraits> = useCallback(
    (email, traits, callback) => {
      if (isImpersonatedUser) {
        logger.debug('[useEventTracking.enhancedIdentify] skipping identify because current user is impersonated');
        return;
      }
      _eventTracker.identify(email, { ...traits }, callback);
    },
    [isImpersonatedUser]
  );

  /**
   * Enhance each linkUserWithGroup call with proper typings for user traits
   * Also prevents impersonated sessions from being linked.
   */
  const enhancedLinkUserWithGroup: typeof _eventTracker.linkUserWithGroup<IdentifyUserTraits> = useCallback(
    (groupId, traits, callback) => {
      if (isImpersonatedUser) {
        logger.debug(
          '[useEventTracking.enhancedLinkUserWithGroup] skipping linkUserWithGroup because current user is impersonated'
        );
        return;
      }
      _eventTracker.linkUserWithGroup(groupId, { ...traits }, callback);
    },
    [isImpersonatedUser]
  );

  /**
   * Enhance each alias call by preventing impersonated sessions from being aliased.
   */
  const enhancedAlias = useCallback(
    (newId: string, oldId: string, callback?: () => void) => {
      if (isImpersonatedUser) {
        logger.debug('[useEventTracking.enhancedAlias] skipping alias because current user is impersonated');
        return;
      }
      _eventTracker.alias(newId, oldId, callback);
    },
    [isImpersonatedUser]
  );

  /**
   * IMPORTANT NOTE: Because we are re-exporting some class method as an object property, we have to re-bind the context
   * of the Event Tracker class to the exported method (ie: `method.bind(classInstance)`)). Otherwise, the exported
   * method will not have access to the class's properties/methods and `this` will be undefined because it will be
   * called from the global scope, resulting runtime errors.
   *
   * Note that arrow functions do not have their own `this`
   * context, so they do not need to be re-bound. (eg. enhancedTrack and enhancedViewedPage above)
   */
  return useMemo(
    () => ({
      track: enhancedTrack,
      alias: enhancedAlias, // updated enhanced alias
      clearIdentity: _eventTracker.clearIdentity.bind(_eventTracker),
      identify: enhancedIdentify,
      linkUserWithGroup: enhancedLinkUserWithGroup,
      viewedPage: enhancedViewedPage,
    }),
    [enhancedTrack, enhancedViewedPage, enhancedIdentify, enhancedLinkUserWithGroup, enhancedAlias]
  );
};
